import { cn } from './utils';


/**
 * Shared list-page state primitives (audit 5e77258d). The role-gated no-access
 * state, the centered empty-state shell, and the skeleton-row loop were
 * re-implemented per page (Sales / Customers / Inventory / Listings). Promoted
 * here per styleguide §6; each page passes its own copy / CTA / skeleton cells so
 * rendered output is unchanged.
 */


/** Role-gated no-access state (e.g. a Lister on Sales/Customers) — fires no
 * queries. Only the copy varies per page. */
function PermissionDenied({ title, description }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 py-24 text-center">
      <span className="text-card-title font-semibold text-ink">{title}</span>
      <span className="text-table text-ink-muted">{description}</span>
    </div>
  );
}

PermissionDenied.displayName = 'PermissionDenied';


/** Centered empty-state shell: title + description + optional CTA (`children`).
 * `gapClassName` (default gap-3) and `descriptionClassName` cover the per-page
 * spacing / max-width differences. */
function EmptyState({ title, description, gapClassName = 'gap-3', descriptionClassName, children }) {
  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center py-20 text-center', gapClassName)}>
      <span className="text-card-title font-semibold text-ink">{title}</span>
      <span className={cn('text-table text-ink-muted', descriptionClassName)}>{description}</span>
      {children}
    </div>
  );
}

EmptyState.displayName = 'EmptyState';


/** Skeleton placeholder rows shown while a list loads. `wrapperClassName` /
 * `rowClassName` set the per-table layout; `children` are the per-row skeleton
 * cells (rendered inside each of `count` rows). */
function SkeletonRows({ count = 8, wrapperClassName = 'flex flex-col', rowClassName, children }) {
  return (
    <div className={wrapperClassName}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={rowClassName}>
          {children}
        </div>
      ))}
    </div>
  );
}

SkeletonRows.displayName = 'SkeletonRows';


export { EmptyState, PermissionDenied, SkeletonRows };
